import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  OutlinedInput,
} from '@mui/material';
import { Organisation, organisationsApi } from '../../../../api';

interface EditOrganisationDialogProps {
  open: boolean;
  organisation: Organisation;
  onClose: () => void;
  onSuccess: () => void;
}

const EditOrganisationDialog: React.FC<EditOrganisationDialogProps> = ({
  open,
  organisation,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState(organisation);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFormData(organisation);
  }, [organisation]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      setError('Organisation name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await organisationsApi.update(organisation.organisation_id, formData);
      onSuccess();
    } catch (err: any) {
      console.error('Failed to update organisation:', err);
      setError(err.response?.data?.detail || 'Failed to update organisation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Organisation</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              required
              label="Organisation Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={formData.description || ''}
              onChange={(e) => handleChange('description', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Domain"
              value={formData.domain || ''}
              onChange={(e) => handleChange('domain', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Contact Email"
              type="email"
              value={formData.contact_email || ''}
              onChange={(e) => handleChange('contact_email', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Contact Phone"
              value={formData.contact_phone || ''}
              onChange={(e) => handleChange('contact_phone', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Address Line 1"
              value={formData.address_line1 || ''}
              onChange={(e) => handleChange('address_line1', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Address Line 2"
              value={formData.address_line2 || ''}
              onChange={(e) => handleChange('address_line2', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="City"
              value={formData.city || ''}
              onChange={(e) => handleChange('city', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="State/Province"
              value={formData.state || ''}
              onChange={(e) => handleChange('state', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Postal Code"
              value={formData.postal_code || ''}
              onChange={(e) => handleChange('postal_code', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="country-label">Country</InputLabel>
              <Select
                labelId="country-label"
                value={formData.country || ''}
                input={<OutlinedInput label="Country" />}
                onChange={(e) => handleChange('country', e.target.value)}
              >
                <MenuItem value=""><em>— None —</em></MenuItem>
                <MenuItem value="US">🇺🇸 United States</MenuItem>
                <MenuItem value="NO">🇳🇴 Norway</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Subscription Tier</InputLabel>
              <Select
                value={formData.subscription_tier || 'basic'}
                label="Subscription Tier"
                onChange={(e) => handleChange('subscription_tier', e.target.value)}
              >
                <MenuItem value="basic">Basic</MenuItem>
                <MenuItem value="professional">Professional</MenuItem>
                <MenuItem value="enterprise">Enterprise</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Max Users"
              value={formData.max_users || 0}
              onChange={(e) => handleChange('max_users', parseInt(e.target.value) || 0)}
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              type="number"
              label="Max Guidelines"
              value={formData.max_guidelines || 0}
              onChange={(e) => handleChange('max_guidelines', parseInt(e.target.value) || 0)}
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading} variant="ghost">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrganisationDialog;
